import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GlobalSearchComponent = ({ isVisible, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ doctors: [], providers: [] });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (searchQuery.length > 2) {
      performSearch();
    } else {
      setSearchResults({ doctors: [], providers: [] });
    }
  }, [searchQuery]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const [doctorsResponse, providersResponse] = await Promise.all([
        axios.get(`${API}/doctors/search`, { params: { q: searchQuery, limit: 5 } }),
        axios.get(`${API}/healthcare-providers/search`, { params: { q: searchQuery, limit: 5 } })
      ]);

      setSearchResults({
        doctors: doctorsResponse.data.doctors || [],
        providers: providersResponse.data.providers || []
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (type, id) => {
    if (type === 'doctor') {
      navigate('/doctors');
    } else {
      navigate('/healthcare-providers');
    }
    onClose();
  };

  const handleQuickAction = (action) => {
    navigate(action);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-96 overflow-hidden">
        {/* Search Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Search Healthcare</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for doctors, hospitals, clinics..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              autoFocus
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600 mb-3">Quick Actions:</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleQuickAction('/doctors')}
              className="flex items-center p-3 bg-white rounded-lg border hover:bg-blue-50 hover:border-blue-200 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Browse Doctors</p>
                <p className="text-xs text-gray-500">View all specialists</p>
              </div>
            </button>
            
            <button
              onClick={() => handleQuickAction('/healthcare-providers')}
              className="flex items-center p-3 bg-white rounded-lg border hover:bg-green-50 hover:border-green-200 transition-colors"
            >
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Find Healthcare</p>
                <p className="text-xs text-gray-500">Hospitals & clinics</p>
              </div>
            </button>
          </div>
        </div>

        {/* Search Results */}
        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-600">Searching...</p>
            </div>
          )}

          {!loading && searchQuery.length > 2 && (
            <div className="p-4">
              {/* Doctors Results */}
              {searchResults.doctors.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">Doctors</h3>
                    <button
                      onClick={() => handleQuickAction('/doctors')}
                      className="text-blue-600 text-sm hover:text-blue-700"
                    >
                      View all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {searchResults.doctors.map((doctor) => (
                      <button
                        key={doctor.id}
                        onClick={() => handleResultClick('doctor', doctor.id)}
                        className="w-full text-left p-3 rounded-lg hover:bg-gray-50 border transition-colors"
                      >
                        <div className="flex items-center">
                          {doctor.profile_image ? (
                            <img
                              src={doctor.profile_image}
                              alt={doctor.name}
                              className="w-10 h-10 rounded-full object-cover border border-gray-200 mr-3"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-medium">
                                {doctor.name?.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{doctor.name}</p>
                            <p className="text-sm text-gray-500">{doctor.specialty}</p>
                            {doctor.hospital_affiliations?.[0] && (
                              <p className="text-xs text-gray-400">{doctor.hospital_affiliations[0].name}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {doctor.rating && (
                              <div className="flex items-center">
                                <span className="text-yellow-400">★</span>
                                <span className="text-sm text-gray-600 ml-1">{doctor.rating}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Healthcare Providers Results */}
              {searchResults.providers.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">Healthcare Providers</h3>
                    <button
                      onClick={() => handleQuickAction('/healthcare-providers')}
                      className="text-blue-600 text-sm hover:text-blue-700"
                    >
                      View all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {searchResults.providers.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => handleResultClick('provider', provider.id)}
                        className="w-full text-left p-3 rounded-lg hover:bg-gray-50 border transition-colors"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-green-600 font-medium">
                              {provider.name?.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{provider.name}</p>
                            <p className="text-sm text-gray-500">{provider.type} • {provider.category}</p>
                            <p className="text-xs text-gray-400">{provider.area || provider.location?.area}</p>
                          </div>
                          <div className="text-right">
                            {provider.rating && (
                              <div className="flex items-center">
                                <span className="text-yellow-400">★</span>
                                <span className="text-sm text-gray-600 ml-1">{provider.rating}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchResults.doctors.length === 0 && searchResults.providers.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600">No results found for "{searchQuery}"</p>
                  <p className="text-sm text-gray-500 mt-1">Try searching for doctor names, specialties, or hospital names</p>
                </div>
              )}
            </div>
          )}

          {!loading && searchQuery.length <= 2 && (
            <div className="p-6 text-center text-gray-500">
              <p>Type at least 3 characters to search</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchComponent;